import React from 'react';
import { Box, Typography } from '@mui/material';
import { BRAND } from '../../brand';

// ============================================================================
//  Setup form layout primitives
// ============================================================================
//
//  Plain CSS Grid rather than MUI's <Grid>. This project runs MUI 7, where
//  Grid is the v2 API — `item`, `xs` and `md` are silently ignored and every
//  field collapses to its content width. That is what jammed "Ownership" up
//  against "Facility Type" on the Facility step.
//
//  Using CSS Grid directly sidesteps the whole question: a field's width is
//  its column span, the spans always add up to 12, and nothing changes
//  underneath us on the next MUI upgrade.
//
//  Usage
//    <FormSection title="Location" caption="…">
//      <FormRow>
//        <Field span={4}><TextField … /></Field>
//        <Field span={4}><TextField … /></Field>
//        <Field span={4}><TextField … /></Field>
//      </FormRow>
//    </FormSection>

// ── Section: a labelled group of related fields ─────────────────────────────
//    Vertical rhythm tightens on small screens: on a phone the separators cost
//    real estate that would otherwise be a visible field, and the whole point
//    is to keep each step within a screen or two of scrolling.
export function FormSection({ title, caption, children, first = false }) {
  return (
    <Box
      sx={{
        mt:  first ? 0 : { xs: 2.75, md: 3.5 },
        pt:  first ? 0 : { xs: 2.25, md: 3 },
        borderTop: first ? 'none' : `1px solid ${BRAND.line}`,
      }}
    >
      {title && (
        <Box sx={{ mb: { xs: 1.75, md: 2.25 } }}>
          <Typography
            sx={{
              fontSize: { xs: 11, md: 11.5 }, fontWeight: 800, letterSpacing: '1.1px',
              textTransform: 'uppercase', color: BRAND.navy,
            }}
          >
            {title}
          </Typography>
          {caption && (
            <Typography
              sx={{
                fontSize: { xs: 12, md: 12.5 }, color: BRAND.inkMuted,
                mt: .4, lineHeight: 1.6,
                // The caption is orientation, not instruction — on a phone it
                // is the first thing worth sacrificing to a narrow column.
                display: { xs: 'none', sm: 'block' },
              }}
            >
              {caption}
            </Typography>
          )}
        </Box>
      )}
      {children}
    </Box>
  );
}

// ── Row: a 12-column grid that collapses to a single column on phones.
//    Row gap stays larger than column gap so helper text under a field never
//    crowds the field below it.
export function FormRow({ children, sx }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(12, 1fr)' },
        columnGap: { xs: 0, sm: 2, md: 2.5 },
        rowGap:    { xs: 2, sm: 2.25, md: 2.75 },
        alignItems: 'start',
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

// ── Field: one cell.
//
//    `span` is the width at md and up (1–12). Narrower breakpoints are derived
//    rather than declared per field, so the three form steps cannot drift out
//    of agreement about what a tablet looks like:
//
//      xs  (<600px)   one field per row — anything narrower is unusable on a
//                     phone, and a half-width select cannot show its value
//      sm  (600–900)  thirds and halves both become halves; full stays full
//      md+ (≥900px)   the declared span
//
//    minWidth: 0 is load-bearing — without it a grid item refuses to shrink
//    below its content's intrinsic width, which is the other half of why the
//    original rows overflowed instead of dividing evenly.
export function Field({ span = 12, children }) {
  const smSpan = span >= 12 ? 12 : 6;

  return (
    <Box
      sx={{
        minWidth: 0,
        gridColumn: {
          xs: 'span 1',
          sm: `span ${smSpan}`,
          md: `span ${span}`,
        },
        '& .MuiFormControl-root': { width: '100%' },
      }}
    >
      {children}
    </Box>
  );
}

export default FormSection;
